import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import useCannedMessage, {
  CannedMessageItem
} from "../../components/CannedMessage/use-cannedMessage";

const MIN_TEXTAREA_HEIGHT = 28;

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

type UseCannedMessageShortcutParams = {
  enabled: boolean;
  message: string;
  setMessage: (value: string) => void;
  messageRef: RefObject<HTMLTextAreaElement | null>;
};

type ShortcutSuggestionSelectionSource =
  | "keyboard"
  | "mouse"
  | "programmatic";

export default function useCannedMessageShortcut({
  enabled,
  message,
  setMessage,
  messageRef
}: UseCannedMessageShortcutParams) {
  const { getAllCannedMessages } = useCannedMessage();
  const [showCannedMessageModal, setShowCannedMessageModal] = useState(false);
  const [cannedMessages, setCannedMessages] = useState<CannedMessageItem[]>([]);
  const [shortcutSuggestions, setShortcutSuggestions] = useState<
    CannedMessageItem[]
  >([]);
  const [shortcutSuggestionsOffset, setShortcutSuggestionsOffset] = useState(0);
  const [shortcutSuggestionsHasMore, setShortcutSuggestionsHasMore] =
    useState(false);
  const [
    selectedShortcutSuggestionIndex,
    setSelectedShortcutSuggestionIndex
  ] = useState(-1);
  const [shortcutSuggestionsLoading, setShortcutSuggestionsLoading] =
    useState(false);
  const [cannedMessagesSearch, setCannedMessagesSearch] = useState("");
  const [cannedMessagesOffset, setCannedMessagesOffset] = useState(0);
  const [cannedMessagesHasMore, setCannedMessagesHasMore] = useState(true);
  const [cannedMessagesLoading, setCannedMessagesLoading] = useState(false);
  const [cannedMessageMode, setCannedMessageMode] = useState<
    "list" | "create" | "edit"
  >("list");
  const [selectedCannedMessage, setSelectedCannedMessage] =
    useState<CannedMessageItem | null>(null);
  const cannedMessagesLimit = 10;
  const shortcutSuggestionsLimit = 4;
  const debouncedCannedMessagesSearch = useDebounce(cannedMessagesSearch, 600);
  const shortcutMatch = message.match(/(?:^|\s)\/([^\s/]*)$/);
  const hasShortcutTrigger = shortcutMatch !== null;
  const shortcutSearch = shortcutMatch ? shortcutMatch[1] ?? "" : null;
  const debouncedShortcutSearch = useDebounce(shortcutSearch ?? "", 600);
  const latestShortcutSearchRef = useRef<string | null>(null);
  const selectedShortcutSuggestionIndexRef = useRef(-1);
  const pendingSelectNextShortcutSuggestionRef = useRef(false);
  const shortcutSuggestionSelectionSourceRef =
    useRef<ShortcutSuggestionSelectionSource>("programmatic");
  const shortcutSuggestionsListRef = useRef<HTMLDivElement | null>(null);
  const shortcutSuggestionItemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const updateSelectedShortcutSuggestionIndex = useCallback(
    (
      index: number,
      source: ShortcutSuggestionSelectionSource = "programmatic"
    ) => {
      shortcutSuggestionSelectionSourceRef.current = source;
      setSelectedShortcutSuggestionIndex(index);
    },
    []
  );

  const handleShortcutSuggestionMouseEnter = useCallback((index: number) => {
    updateSelectedShortcutSuggestionIndex(index, "mouse");
  }, [updateSelectedShortcutSuggestionIndex]);

  const setComposerMessage = (value: string) => {
    setMessage(value);

    window.setTimeout(() => {
      if (messageRef.current) {
        messageRef.current.style.height = `${MIN_TEXTAREA_HEIGHT}px`;
        messageRef.current.style.height = `${messageRef.current.scrollHeight}px`;
      }
    }, 0);
  };

  const applyCannedMessageShortcut = (cannedMessage: CannedMessageItem) => {
    setShortcutSuggestions([]);
    setShortcutSuggestionsOffset(0);
    setShortcutSuggestionsHasMore(false);
    updateSelectedShortcutSuggestionIndex(-1);
    setSelectedCannedMessage(cannedMessage);
    setComposerMessage(cannedMessage.message);
    messageRef.current?.focus();
  };

  const setShortcutSuggestionItemRef =
    (index: number) => (node: HTMLButtonElement | null) => {
      shortcutSuggestionItemRefs.current[index] = node;
    };

  useEffect(() => {
    selectedShortcutSuggestionIndexRef.current = selectedShortcutSuggestionIndex;
  }, [selectedShortcutSuggestionIndex]);

  useEffect(() => {
    if (!selectedCannedMessage) {
      return;
    }

    if (message !== selectedCannedMessage.message) {
      setSelectedCannedMessage(null);
    }
  }, [message, selectedCannedMessage]);

  useEffect(() => {
    const fetchCannedMessages = async () => {
      try {
        setCannedMessagesLoading(true);
        const response = await getAllCannedMessages({
          search: debouncedCannedMessagesSearch,
          offset: 0,
          limit: cannedMessagesLimit
        });
        setCannedMessages(response.result);
        setCannedMessagesOffset(response.result.length);
        setCannedMessagesHasMore(response.result.length < response.total);
      } finally {
        setCannedMessagesLoading(false);
      }
    };

    setCannedMessagesOffset(0);
    setCannedMessagesHasMore(true);

    fetchCannedMessages();
  }, [debouncedCannedMessagesSearch, getAllCannedMessages]);

  const loadMoreCannedMessages = async () => {
    if (cannedMessagesLoading || !cannedMessagesHasMore) return;

    try {
      setCannedMessagesLoading(true);
      const currentOffset = cannedMessagesOffset;
      const response = await getAllCannedMessages({
        search: debouncedCannedMessagesSearch,
        offset: currentOffset,
        limit: cannedMessagesLimit
      });

      setCannedMessages((prev) => [...prev, ...response.result]);
      setCannedMessagesOffset(currentOffset + response.result.length);

      if (currentOffset + response.result.length >= response.total) {
        setCannedMessagesHasMore(false);
      }
    } finally {
      setCannedMessagesLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchShortcutSuggestions = async () => {
      if (!enabled || !hasShortcutTrigger) {
        setShortcutSuggestions([]);
        setShortcutSuggestionsOffset(0);
        setShortcutSuggestionsHasMore(false);
        updateSelectedShortcutSuggestionIndex(-1);
        setShortcutSuggestionsLoading(false);
        latestShortcutSearchRef.current = null;
        pendingSelectNextShortcutSuggestionRef.current = false;
        return;
      }

      const requestSearch = debouncedShortcutSearch;
      latestShortcutSearchRef.current = requestSearch;

      try {
        setShortcutSuggestionsLoading(true);
        const response = await getAllCannedMessages({
          search: requestSearch,
          offset: 0,
          limit: shortcutSuggestionsLimit
        });

        if (cancelled || latestShortcutSearchRef.current !== requestSearch) {
          return;
        }

        setShortcutSuggestions(response.result);
        setShortcutSuggestionsOffset(response.result.length);
        setShortcutSuggestionsHasMore(response.result.length < response.total);
        updateSelectedShortcutSuggestionIndex(response.result.length ? 0 : -1);
        pendingSelectNextShortcutSuggestionRef.current = false;
        if (shortcutSuggestionsListRef.current) {
          shortcutSuggestionsListRef.current.scrollTop = 0;
        }
      } finally {
        if (!cancelled) {
          setShortcutSuggestionsLoading(false);
        }
      }
    };

    fetchShortcutSuggestions();

    return () => {
      cancelled = true;
    };
  }, [
    debouncedShortcutSearch,
    enabled,
    getAllCannedMessages,
    hasShortcutTrigger,
    shortcutSuggestionsLimit
  ]);

  const loadMoreShortcutSuggestions = useCallback(
    async (options?: { selectNext?: boolean }) => {
      if (
        shortcutSuggestionsLoading ||
        !shortcutSuggestionsHasMore ||
        !enabled ||
        !hasShortcutTrigger
      ) {
        return;
      }

      const requestSearch = debouncedShortcutSearch;
      const currentOffset = shortcutSuggestionsOffset;
      pendingSelectNextShortcutSuggestionRef.current = Boolean(
        options?.selectNext
      );

      try {
        setShortcutSuggestionsLoading(true);
        const response = await getAllCannedMessages({
          search: requestSearch,
          offset: currentOffset,
          limit: shortcutSuggestionsLimit
        });

        if (latestShortcutSearchRef.current !== requestSearch) {
          pendingSelectNextShortcutSuggestionRef.current = false;
          return;
        }

        const existingIds = new Set(shortcutSuggestions.map((item) => item.id));
        const appendedShortcutSuggestions = response.result.filter(
          (item) => !existingIds.has(item.id)
        );
        const nextShortcutSuggestions = [
          ...shortcutSuggestions,
          ...appendedShortcutSuggestions
        ];

        setShortcutSuggestions(nextShortcutSuggestions);

        setShortcutSuggestionsOffset(currentOffset + response.result.length);
        setShortcutSuggestionsHasMore(
          response.result.length > 0 &&
            currentOffset + response.result.length < response.total
        );

        if (
          pendingSelectNextShortcutSuggestionRef.current &&
          appendedShortcutSuggestions.length > 0
        ) {
          updateSelectedShortcutSuggestionIndex(
            Math.min(
              selectedShortcutSuggestionIndexRef.current + 1,
              nextShortcutSuggestions.length - 1
            ),
            "keyboard"
          );
        }

        pendingSelectNextShortcutSuggestionRef.current = false;
      } finally {
        setShortcutSuggestionsLoading(false);
      }
    },
    [
      debouncedShortcutSearch,
      enabled,
      getAllCannedMessages,
      hasShortcutTrigger,
      shortcutSuggestions,
      shortcutSuggestionsHasMore,
      shortcutSuggestionsLimit,
      shortcutSuggestionsLoading,
      shortcutSuggestionsOffset
    ]
  );

  useEffect(() => {
    if (selectedShortcutSuggestionIndex < 0) {
      return;
    }

    if (shortcutSuggestionSelectionSourceRef.current !== "keyboard") {
      return;
    }

    const container = shortcutSuggestionsListRef.current;
    const item =
      shortcutSuggestionItemRefs.current[selectedShortcutSuggestionIndex];

    if (!container || !item) {
      return;
    }

    const itemTop = item.offsetTop - container.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const viewportTop = container.scrollTop;
    const viewportBottom = viewportTop + container.clientHeight;

    if (itemTop < viewportTop) {
      container.scrollTop = Math.max(0, itemTop);
    } else if (itemBottom > viewportBottom) {
      container.scrollTop = itemBottom - container.clientHeight;
    }
  }, [selectedShortcutSuggestionIndex, shortcutSuggestions]);

  useEffect(() => {
    if (
      shortcutSuggestionSelectionSourceRef.current === "keyboard" &&
      selectedShortcutSuggestionIndex >= 0 &&
      selectedShortcutSuggestionIndex === shortcutSuggestions.length - 1 &&
      shortcutSuggestionsHasMore &&
      !shortcutSuggestionsLoading
    ) {
      void loadMoreShortcutSuggestions();
    }
  }, [
    loadMoreShortcutSuggestions,
    selectedShortcutSuggestionIndex,
    shortcutSuggestions.length,
    shortcutSuggestionsHasMore,
    shortcutSuggestionsLoading
  ]);

  const handleShortcutSuggestionsKeyDown = (
    e: KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (!enabled || !hasShortcutTrigger || shortcutSuggestions.length === 0) {
      return false;
    }

    if (e.key === "ArrowDown") {
      if (selectedShortcutSuggestionIndex < shortcutSuggestions.length - 1) {
        updateSelectedShortcutSuggestionIndex(
          Math.min(
            selectedShortcutSuggestionIndexRef.current + 1,
            shortcutSuggestions.length - 1
          ),
          "keyboard"
        );
      } else if (shortcutSuggestionsHasMore && !shortcutSuggestionsLoading) {
        void loadMoreShortcutSuggestions({ selectNext: true });
      }

      return true;
    }

    if (e.key === "ArrowUp") {
      updateSelectedShortcutSuggestionIndex(
        Math.max(selectedShortcutSuggestionIndexRef.current - 1, 0),
        "keyboard"
      );
      return true;
    }

    if (e.key === "Enter" && selectedShortcutSuggestionIndex >= 0) {
      const selectedShortcutSuggestion =
        shortcutSuggestions[selectedShortcutSuggestionIndex];

      if (selectedShortcutSuggestion) {
        applyCannedMessageShortcut(selectedShortcutSuggestion);
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (!hasShortcutTrigger || shortcutSearch === null) {
      return;
    }

    const normalizedShortcut = shortcutSearch.trim().toLowerCase();

    if (!normalizedShortcut) {
      return;
    }

    const exactMatch = shortcutSuggestions.find(
      (item) => item.shortcut.trim().toLowerCase() === normalizedShortcut
    );

    if (!exactMatch) {
      return;
    }

    const trimmedMessage = message.trim();

    if (!trimmedMessage.endsWith(`/${shortcutSearch}`)) {
      return;
    }

    setShortcutSuggestions([]);
    setSelectedCannedMessage(exactMatch);
    setComposerMessage(exactMatch.message);
    messageRef.current?.focus();
  }, [hasShortcutTrigger, message, messageRef, shortcutSearch, shortcutSuggestions]);

  const handleCreateSuccess = (newMessage: CannedMessageItem) => {
    setCannedMessages((prev) => [...prev, newMessage]);
  };

  const handleUpdateSuccess = (updatedMessage: CannedMessageItem) => {
    setCannedMessages((prev) =>
      prev.map((item) => (item.id === updatedMessage.id ? updatedMessage : item))
    );
  };

  const handleDeleteSuccess = (deletedId: number) => {
    setCannedMessages((prev) => prev.filter((item) => item.id !== deletedId));
  };

  return {
    showCannedMessageModal,
    setShowCannedMessageModal,
    cannedMessages,
    shortcutSuggestions,
    shortcutSuggestionsHasMore,
    shortcutSuggestionsLoading,
    selectedShortcutSuggestionIndex,
    cannedMessagesSearch,
    setCannedMessagesSearch,
    cannedMessagesHasMore,
    cannedMessagesLoading,
    cannedMessageMode,
    setCannedMessageMode,
    hasShortcutTrigger,
    applyCannedMessageShortcut,
    handleShortcutSuggestionsKeyDown,
    loadMoreShortcutSuggestions,
    loadMoreCannedMessages,
    setSelectedShortcutSuggestionIndex: updateSelectedShortcutSuggestionIndex,
    handleShortcutSuggestionMouseEnter,
    setShortcutSuggestionItemRef,
    handleCreateSuccess,
    handleUpdateSuccess,
    handleDeleteSuccess,
    shortcutSuggestionsListRef,
    setComposerMessage,
    selectedCannedMessageId: selectedCannedMessage?.id
  };
}
